import { Divider, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  formatReviewReason,
  TARGETED_REVIEW_REASON_LABEL_KEYS,
} from "components/manualReview/utils/reviewReasons";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import {
  ModelState,
  useGetEventLabellingModelStateHistoriesQuery,
} from "store/api/generatedApi";

dayjs.extend(utc);

const formatStateRange = (
  state: ModelState,
  t: ReturnType<typeof useTenantTranslation>["t"]
) => {
  // Both bounds unset is the withdrawal, so it must not render as an unbounded
  // range — and an unguarded dayjs of a null bound prints "Invalid Date".
  if (state.effective_from === null && state.effective_to === null) {
    return t("modelStateNotApplied");
  }
  const from =
    state.effective_from === null
      ? t("modelStateOpenStart")
      : dayjs.utc(state.effective_from).format("YYYY-MM-DD HH:mm:ss");
  const to =
    state.effective_to === null
      ? t("modelStateOpenEnded")
      : dayjs.utc(state.effective_to).format("YYYY-MM-DD HH:mm:ss");
  return `${from} → ${to}`;
};

export const ModelStateHistory = ({ modelId }: { modelId: number }) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  // The endpoint ships in the same PR as this call site, and the two deploy
  // separately: an older backend 404s, which reads as an empty history.
  const { data } = useGetEventLabellingModelStateHistoriesQuery({
    tenantName,
    modelId: [modelId],
  });
  const history: readonly ModelState[] =
    data?.histories.find((entry) => entry.model_id === modelId)?.states ?? [];

  return (
    <Stack spacing={1} sx={{ maxWidth: 480 }}>
      <Typography variant="subtitle2">{t("modelStateHistory")}</Typography>
      {history.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("noModelStateHistory")}
        </Typography>
      ) : (
        <Stack
          divider={<Divider />}
          sx={{ border: 1, borderColor: "grey.300", borderRadius: 1 }}
        >
          <Stack direction="row" spacing={2} sx={{ px: 1.5, py: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flex: 3 }}
            >
              {t("modelStateAppliesFrom")}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flex: 1 }}
            >
              {t("authority")}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flex: 2 }}
            >
              {t("inferenceEnvironment")}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flex: 2 }}
            >
              {t("modelStatus")}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flex: 3 }}
            >
              {t("flagRules")}
            </Typography>
          </Stack>
          {history.map((state) => (
            <Stack
              key={state.id}
              direction="row"
              spacing={2}
              sx={{ px: 1.5, py: 1 }}
            >
              <Typography variant="body2Mono" sx={{ flex: 3 }}>
                {formatStateRange(state, t)}
              </Typography>
              <Typography variant="body2Mono" sx={{ flex: 1 }}>
                {state.authority ?? t("none")}
              </Typography>
              <Typography variant="body2" sx={{ flex: 2 }}>
                {t(state.inference_environment)}
              </Typography>
              <Typography variant="body2" sx={{ flex: 2 }}>
                {t(state.status)}
              </Typography>
              <Typography variant="body2" sx={{ flex: 3 }}>
                {state.flag_rules === null
                  ? t("none")
                  : state.flag_rules.reasons
                      .map((reason) => {
                        const labelKey =
                          TARGETED_REVIEW_REASON_LABEL_KEYS[reason];
                        return labelKey
                          ? t(labelKey)
                          : formatReviewReason(reason);
                      })
                      .join(", ")}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
